import React, { useMemo } from 'react';
import type { SearchMatch } from '../utils/searchUtils';

interface HighlightedTextProps {
  text: string;
  searchQuery: string;
  textType: 'original' | 'translation';
  segmentMatches: SearchMatch[];
  currentMatch: SearchMatch | null;
}

/**
 * Component that renders text with highlighted search matches
 */
const HighlightedText: React.FC<HighlightedTextProps> = ({
  text,
  searchQuery,
  textType,
  segmentMatches,
  currentMatch,
}) => {
  const highlightedContent = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '' || !text) {
      return <>{text}</>;
    }

    // Filter matches for this text type
    const relevantMatches = segmentMatches.filter(m => m.textType === textType);

    if (relevantMatches.length === 0) {
      return <>{text}</>;
    }

    // Sort matches by startOffset
    const sortedMatches = [...relevantMatches].sort((a, b) => a.startOffset - b.startOffset);

    // Build the highlighted text
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    sortedMatches.forEach((match, idx) => {
      // Add text before this match
      if (match.startOffset > lastIndex) {
        parts.push(
          <span key={`text-${idx}`}>
            {text.slice(lastIndex, match.startOffset)}
          </span>
        );
      }

      // Check if this is the current match
      const isCurrent = currentMatch !== null
        && currentMatch.textType === textType
        && currentMatch.startOffset === match.startOffset
        && currentMatch.length === match.length;

      // Add the highlighted match
      parts.push(
        <mark
          key={`match-${idx}`}
          className={
            isCurrent
              ? 'bg-orange-400 text-orange-900 rounded px-0.5'
              : 'bg-yellow-200 text-yellow-900 rounded px-0.5'
          }
          data-current-match={isCurrent ? 'true' : undefined}
        >
          {text.slice(match.startOffset, match.startOffset + match.length)}
        </mark>
      );

      lastIndex = match.startOffset + match.length;
    });

    // Add any remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(
        <span key="text-end">{text.slice(lastIndex)}</span>
      );
    }

    return <>{parts}</>;
  }, [text, searchQuery, textType, segmentMatches, currentMatch]);

  return highlightedContent;
};

export default HighlightedText;
